import { useState } from 'react';
import { LogOut, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PINDialog } from './PINDialog';
import { useViewMode } from '../../context/ViewModeContext';
import type { HAConnectionState } from '../../ha/api';
import type { ActiveMode } from '../../hooks/useAdaptiveHAConnection';

export interface HeaderProps {
  connectionState: HAConnectionState;
  disabledReason?: string;
  locationName?: string;
  activeMode?: ActiveMode;
  /** Revokes the current HA session and forces a fresh login. Absent while unauthenticated. */
  onLogout?: () => void;
}

function NetworkModeBadge({ activeMode }: { activeMode?: ActiveMode }): JSX.Element | null {
  if (!activeMode) return null;

  const isLan = activeMode === 'lan';
  const label = isLan ? 'LAN' : 'WAN';
  const classes = isLan
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
    : 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400';

  return (
    <span
      className={`rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${classes}`}
      data-testid="network-mode-badge"
      data-active-mode={activeMode}
      title={
        isLan
          ? 'Direkte LAN-Verbindung zum Home Assistant'
          : 'Fallback ueber WAN (Pangolin)'
      }
    >
      {label}
    </span>
  );
}

function ConnectionBadge({
  connectionState,
  disabledReason,
}: {
  connectionState: HAConnectionState;
  disabledReason?: string;
}): JSX.Element {
  let dotClass = 'bg-gray-500';
  let label = 'Bereit';

  if (disabledReason === 'mockRegistry') {
    dotClass = 'bg-amber-400';
    label = 'Mock';
  } else if (connectionState === 'auth_invalid') {
    dotClass = 'bg-rose-500';
    label = 'Abgemeldet';
  } else if (connectionState === 'connected') {
    dotClass = 'bg-emerald-500';
    label = 'Verbunden';
  } else if (connectionState === 'connecting' || connectionState === 'reconnecting') {
    dotClass = 'bg-amber-400 animate-pulse';
    label = 'Verbinden…';
  } else if (
    connectionState === 'failed' ||
    connectionState === 'disconnected' ||
    connectionState === 'closed'
  ) {
    dotClass = 'bg-rose-500';
    label = 'Getrennt';
  }

  return (
    <div
      className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
      data-testid="connection-status"
      data-connection-state={connectionState}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function RSCLogo(): JSX.Element {
  // Offizielles RSControl-Emblem (zwei ueberlagerte Rauten + Zentralpunkt).
  return (
    <div className="flex h-10 w-10 items-center justify-center">
      <svg className="h-9 w-9" viewBox="16 152 328 282" aria-hidden="true">
        <polygon points="180,176 320,250 180,324 40,250" fill="#3F7FE0" fillOpacity="0.16" stroke="#3F7FE0" strokeWidth="14" strokeLinejoin="round"/>
        <polygon points="180,262 320,336 180,410 40,336" fill="#D40F16" fillOpacity="0.16" stroke="#D40F16" strokeWidth="14" strokeLinejoin="round"/>
        <circle cx="180" cy="293" r="16" fill="#3F7FE0"/>
      </svg>
    </div>
  );
}

export function Header({
  connectionState,
  disabledReason,
  locationName,
  activeMode,
  onLogout,
}: HeaderProps): JSX.Element {
  const { isElectrician } = useViewMode();
  const [pinOpen, setPinOpen] = useState(false);

  return (
    <>
      <header
        data-testid="rsc-header"
        className="sticky top-0 z-40 flex items-center justify-between border-b border-gray-200/60 bg-white/70 px-4 py-3 backdrop-blur-md dark:border-gray-700/50 dark:bg-gray-900/70"
      >
        <div className="flex items-center gap-3">
          <RSCLogo />
          <div className="flex flex-col leading-tight">
            <span className="text-lg font-bold leading-tight" data-testid="rsc-wordmark">
              <span className="text-[#D40F16]">RS</span>
              <span className="text-[#4B5563] dark:text-[#F0F0F0]">Control</span>
              <span className="ml-1 text-xs font-medium tracking-wider text-[#3F7FE0]">hub</span>
            </span>
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              data-testid="rsc-location"
            >
              {locationName || "RSC-Anlage"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <NetworkModeBadge activeMode={activeMode} />
          <ConnectionBadge
            connectionState={connectionState}
            disabledReason={disabledReason}
          />
          <Button
            variant={isElectrician ? 'secondary' : 'ghost'}
            size="icon"
            onClick={() => setPinOpen(true)}
            aria-label={
              isElectrician ? 'Elektriker-Modus (aktiv)' : 'Elektriker-Anmeldung öffnen'
            }
            data-testid="header-usercog"
            data-active={isElectrician}
          >
            <UserCog className="h-4 w-4" aria-hidden="true" />
          </Button>
          {onLogout && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onLogout}
              aria-label="Von Home Assistant abmelden"
              data-testid="header-logout"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </header>
      <PINDialog open={pinOpen} onOpenChange={setPinOpen} />
    </>
  );
}
